import { Labels } from '../../global/labels';
import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { setData } from '../../global/ui-helpers';
import { UIHint } from '../hint/ui-hint';
import { UIField } from '../field/ui-field';
import '../textcounter/ui-textcounter';
import { UIRating } from '../rating/ui-rating';
import { UIButtonbar } from '../buttonbar/ui-buttonbar';
import { Digest } from '../../global/digest';
import { EventObserver } from '../../global/event-observer';
import { TabIndex } from '../../global/keyboard';
import {
    validateFeedbackLayout,
    validateFeedbackExtensionLayout,
} from './ui-feedback.helpers';
import type {
    UIFeedbackResult,
    UIFeedbackResults,
    UIFeedbackLayouts,
    UIFeedbackExtensionLayouts,
} from './ui-feedback.type';
import type {
    AttributeValue,
    ObservedAttributes,
    ElementConfig,
} from '../ui-element.type';
import type { TranslationLabels } from '../../types/labels.type';
import type { NullOr, UndefinedOr } from '../../global/utils.type';
import type { UITextCounter } from '../textcounter/ui-textcounter';
import styles from './ui-feedback.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIFeedback
 * @element ui-feedback
 * @classdesc Represents a class for <code>ui-feedback</code> element.
 * User interface for feedback form.
 * @fires event:change
 * @fires event:confirm
 * @property {("default" | "simple")} [layout="default"] {@attr layout} Layout of the feedback.
 *  {@desc default: 1-5 rating layout}
 *  {@desc simple: yes/no layout}
 * @property {("default" | "positive" | "negative")} [extensionLayout]
 * {@attr extension-layout} Feedbacks extension Layout, differs for "simple" feedback.
 * If there is no "extension-layout" - no extension will be rendered.
 *  {@desc default: display extension after any first action}
 *  {@desc positive: display extension only after positive action}
 *  {@desc negative: display extension only after negative action}
 * @property {string} [label] {@attr label} Label for feedback heading.
 * @property {string} [labelMinrating] {@attr label-minrating} Label for minimum
 * label of ui-rating.
 * @property {string} [labelMaxrating] {@attr label-maxrating} Label for maximum
 * label of ui-rating.
 * @property {string} [labelTextarea] {@attr label-textarea} Label for textarea input.
 * @property {string} [labelPositive] {@attr label-positive} Label for simple layout.
 * @property {string} [labelNegative] {@attr label-negative} Label for simple layout.
 * @property {string} [labelHint] {@attr label-hint} Content for ui-hint.
 * @property {string} [labelConfirm] {@attr label-confirm} Label for confirm button.
 * @property {("success" | "error" | null)} [result] {@attr result}
 *  {@desc success}
 *  {@desc error}
 * @property {string} [labelSuccess] {@attr label-success} Label for success.
 * @property {string} [labelCancel] {@attr label-cancel} Label for cancel button.
 * @property {string} [labelError] {@attr label-error} Label for error.
 * @property {string} [labelResult] {@attr label-result} Label for result details.
 * @property {string} [labelCharactersRemaining] {@attr label-characters-remaining} Label for
 * characters remaining.
 * @property {string} [labelLimitValidation] {@attr label-limit-validation} Label for
 * validation error message.
 * @property {string} [labelMessagePlaceholder] {@attr label-message-placeholder} Label for
 * text placeholder
 * @property {string} [labelMissingRating] {@attr label-message-placeholder} Label for
 * missing rating error
 * @property {number} [textMaxlength] {@attr text-maxlength} Textarea counter limit.
 * @property {boolean} [extended=false] {@attr extended} Activate additionally to rating bar
 * - textarea field.
 * @property {boolean} [autoExtended=false] {@attr auto-extended} Display extended layout by default
 * regardless rating activation rule.
 * @property {boolean} [overflowMaxlength=false] {@attr extended} allow to overflow max length
 * of feedback message
 * @property {boolean} [cancelable=false] {@attr cancelable} allow to cancel feedback form
 * @property {HTMLTextAreaElement} extTextarea
 * @property {HTMLButtonElement} submitBtn
 * @property {UIField} ratingBar
 * @property {UIField} extension
 * @property {UIButtonbar} buttonBar
 * @property {UIHint} ratingErrorHint
 * @property {HTMLHeadingElement} heading
 * @example
 * <ui-feedback label="How was it?" extended="extended"></ui-feedback>
 */
@Component({ tag: 'ui-feedback', styles })
export class UIFeedback extends UIElement {
    /**
     * Result states for the feedback.
     */
    static readonly Result: UIFeedbackResults = {
        Success: 'success',
        Error: 'error',
    };

    /**
     * @type {Array<string>}
     */
    static get observedAttributes(): ObservedAttributes {
        return [
            'label',
            'label-minrating',
            'label-maxrating',
            'label-textarea',
            'label-hint',
            'label-confirm',
            'result',
            'label-success',
            'label-error',
            'label-result',
            'label-positive',
            'label-negative',
            'label-characters-remaining',
            'label-message-placeholder',
            'label-limit-validation',
            'label-missing-rating',
            'text-maxlength',
            'extended',
            'layout',
            'extension-layout',
            'overflow-maxlength',
        ];
    }

    /**
     * Layouts enum
     * @type {{SIMPLE: string, DEFAULT: string}}
     */
    static get layouts(): UIFeedbackLayouts {
        return {
            SIMPLE: 'simple',
            DEFAULT: 'default',
        };
    }

    /**
     * Extension layouts enum
     * @type {{DEFAULT: string, POSITIVE: string, NEGATIVE: string}}
     */
    static get extLayouts(): UIFeedbackExtensionLayouts {
        return {
            DEFAULT: 'default',
            POSITIVE: 'positive',
            NEGATIVE: 'negative',
        };
    }

    /**
     * Define labels what could be localised
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-feedback', {
            MIN_RATE: 'Difficult',
            MAX_RATE: 'Easy',
            POSITIVE: 'Yes',
            NEGATIVE: 'No',
            CONFIRM: 'Send',
            CANCEL: 'Cancel',
            TEXTAREA_LABEL: 'What can we do to make it even better?',
            HINT_MESSAGE:
                'This feedback is anonymous.' +
                ' We will use it to improve our services.',
            SUCCESS_MESSAGE: 'Thank you for your feedback!',
            ERROR_MESSAGE: 'Feedback sending failed',
            MISSING_RATING_ERROR: 'Please select a rating',
        });
    }

    @Attribute(String) label?: string;
    @Attribute(String) labelMinrating?: string;
    @Attribute(String) labelMaxrating?: string;
    @Attribute(String) labelTextarea?: string;
    @Attribute(String) labelHint?: string;
    @Attribute(String) labelConfirm?: string;
    @Attribute(String) result?: NullOr<UIFeedbackResult>;
    @Attribute(String) labelSuccess?: string;
    @Attribute(String) labelCancel?: string;
    @Attribute(String) labelError?: string;
    @Attribute(String) labelResult?: string;
    @Attribute(String) labelCharactersRemaining?: string;
    @Attribute(String) labelLimitValidation?: string;
    @Attribute(String) labelMessagePlaceholder?: string;
    @Attribute(String) labelMissingRating?: string;
    @Attribute(String) labelPositive?: string;
    @Attribute(String) labelNegative?: string;
    @Attribute(Number) textMaxlength?: number;
    @Attribute(Boolean) extended?: boolean;
    @Attribute(Boolean) autoExtended?: boolean;
    @Attribute(Boolean) cancelable?: boolean;
    @Attribute(Boolean) overflowMaxlength?: boolean;
    @Attribute({
        type: String,
        validate: validateFeedbackLayout,
    })
    layout?: string = UIFeedback.layouts.DEFAULT;
    @Attribute({
        type: String,
        validate: validateFeedbackExtensionLayout,
    })
    extensionLayout?: string;

    @Child('ui-field.ui-feedback__rating') ratingBar?: UIField;
    @Child('ui-field.ui-feedback__extension') extension?: UIField;
    @Child('ui-buttonbar') buttonBar?: UIButtonbar;
    @Child('.ui-feedback__extension textarea[name="ui-feedback-textarea"]')
    extTextarea?: HTMLTextAreaElement;
    @Child('button[name="ui-feedback-confirm"]') submitBtn!: HTMLButtonElement;
    @Child('.ui-feedback__rating-error-hint') ratingErrorHint!: UIHint;
    @Child('.ui-feedback__heading') heading?: HTMLHeadingElement;

    private ratingActivated: boolean = false;
    private _id?: string;
    private _simpleRating: NullOr<string> = null;
    private readonly clickObserver = new EventObserver({ name: 'click' });
    private readonly changeObserver = new EventObserver({ name: 'change' });
    private readonly inputObserver = new EventObserver({ name: 'input' });
    private readonly feedbackActionHandler = this.handleFeedbackAction.bind(
        this
    ) as EventListener;
    private readonly feedbackConfirmHandler =
        this.handleFeedbackConfirm.bind(this);
    private readonly feedbackCancelHandler =
        this.handleFeedbackCancel.bind(this);
    private readonly ratingBarActivatedHandler =
        this.handleRatingBarActivated.bind(this);
    private readonly textareaInputHandler = this.handleTextareaInput.bind(this);

    /**
     * Stable id for extension textarea.
     * @private
     */
    get uid(): string {
        return (this._id = this._id || Digest.randomId());
    }

    /**
     * @type {("POSITIVE" | "NEGATIVE")}
     */
    get simpleRating() {
        return this._simpleRating;
    }

    set simpleRating(value) {
        let isExtended;
        this._simpleRating = value;
        this.ratingActivated = true;
        this.dispatchCustomEvent('change', { value: value });

        switch (this.extensionLayout) {
            case UIFeedback.extLayouts.POSITIVE:
                isExtended = value === 'POSITIVE';
                break;
            case UIFeedback.extLayouts.NEGATIVE:
                isExtended = value === 'NEGATIVE';
                break;
            case UIFeedback.extLayouts.DEFAULT:
            default:
                isExtended = !!this.extensionLayout || !!this.extended;
                break;
        }

        if (isExtended) {
            this.rebuildChildren(this.buildSimpleFeedbackElements());
            if (this.extTextarea) {
                this.extTextarea.focus();
            }
        } else {
            this.handleFeedbackConfirm();
        }
    }

    /**
     * @type {string}
     * @readonly
     */
    get rating(): NullOr<string | number> {
        switch (this.layout) {
            case UIFeedback.layouts.SIMPLE:
                return this.simpleRating;
            default:
                return this.ratingBar
                    ? this.ratingBar.querySelector<UIRating>('ui-rating')
                          ?.value || null
                    : null;
        }
    }

    /**
     * Return custom label or empty string if the label disabled or default value
     * @param {string} customValue
     * @param {string | null} defaultValue
     * @returns {string}
     */
    defineLabel(
        customValue: UndefinedOr<string>,
        defaultValue: NullOr<TranslationLabels.Label>
    ): NullOr<TranslationLabels.Label | string> {
        if (customValue === 'false') {
            return '';
        }
        return customValue || defaultValue;
    }

    /**
     * Build config with elements for extension
     * @returns {Array<ElementConfig>}
     */
    buildExtension(): Array<ElementConfig> {
        const isTextareaVisible =
            this.layout && (this.extended || this.extensionLayout);
        /**
         * @type {Array<ElementConfig>}
         */
        const configs: Array<ElementConfig> = [
            {
                element: new UIHint(),
                classList: {
                    'ui-feedback__hint': true,
                },
                children: [
                    this.defineLabel(
                        this.labelHint,
                        UIFeedback.labels.HINT_MESSAGE
                    ),
                ],
            },
            {
                element: new UIButtonbar(),
                attributes: {
                    alignment: 'center',
                },
                children: [
                    this.cancelable
                        ? {
                              tagName: 'button',
                              children: [
                                  this.defineLabel(
                                      this.labelCancel,
                                      UIFeedback.labels.CANCEL
                                  ),
                              ],
                              attributes: {
                                  'type': 'button',
                                  'name': 'ui-feedback-cancel',
                                  'class': 'button -destructive',
                                  'data-event': 'ui-feedback-cancel',
                              },
                          }
                        : null,
                    {
                        tagName: 'button',
                        children: [
                            this.defineLabel(
                                this.labelConfirm,
                                UIFeedback.labels.CONFIRM
                            ),
                        ],
                        attributes: {
                            'type': 'button',
                            'name': 'ui-feedback-confirm',
                            'class': 'button',
                            'data-event': 'ui-feedback-confirm',
                            'disabled':
                                (this.extTextarea &&
                                    !this.extTextarea.validity.valid) ||
                                null,
                        },
                    },
                ],
            },
        ];

        if (isTextareaVisible) {
            configs.unshift(this.buildTextareaElement());
        }

        return configs;
    }

    /**
     * Build config for extended element in feedback form
     * @returns {ElementConfig}
     */
    buildTextareaElement(): ElementConfig {
        const validationLabel = this.defineLabel(
            this.labelLimitValidation,
            null
        );
        const textareaId = `ui-feedback-textarea-${this.uid}`;
        return {
            element: new UIField(),
            classList: {
                'ui-feedback__extension': true,
                '-hidden': !this.ratingActivated && !this.autoExtended,
            },
            attributes: {
                label:
                    this.layout === UIFeedback.layouts.DEFAULT
                        ? this.defineLabel(
                              this.labelTextarea,
                              UIFeedback.labels.TEXTAREA_LABEL
                          )
                        : null,
            },
            children: [
                {
                    tagName: 'ui-textcounter',
                    attributes: {
                        'limit': this.textMaxlength || null,
                        'label': this.labelCharactersRemaining,
                        'label-error': validationLabel,
                        'overflow':
                            validationLabel || this.overflowMaxlength
                                ? true
                                : null,
                        'validate':
                            validationLabel || this.overflowMaxlength
                                ? true
                                : null,
                        'nolabel': validationLabel ? true : null,
                    },
                    children: [
                        {
                            tagName: 'textarea',
                            attributes: {
                                'id': textareaId,
                                'name': 'ui-feedback-textarea',
                                'placeholder':
                                    this.labelMessagePlaceholder || null,
                                'data-event': 'ui-feedback-input',
                                'aria-label':
                                    this.layout === UIFeedback.layouts.SIMPLE
                                        ? this.defineLabel(
                                              this.labelTextarea,
                                              UIFeedback.labels.TEXTAREA_LABEL
                                          )
                                        : null,
                            },
                            children: this.extTextarea?.value
                                ? [this.extTextarea.value]
                                : [],
                        },
                    ],
                },
            ],
        };
    }

    /**
     * Build configs for layout with fields and button for feedback
     * @returns {Array<ElementConfig>}
     */
    buildFeedbackElements(): Array<ElementConfig> {
        const configs: Array<ElementConfig> = [
            {
                tagName: 'h2',
                classList: {
                    'ui-feedback__heading': true,
                },
                children: [this.label!],
            },
            {
                element: new UIField(),
                classList: {
                    'ui-feedback__rating': true,
                },
                children: [
                    {
                        element: new UIRating(),
                        attributes: {
                            'label-min': this.defineLabel(
                                this.labelMinrating,
                                UIFeedback.labels.MIN_RATE
                            ),
                            'label-max': this.defineLabel(
                                this.labelMaxrating,
                                UIFeedback.labels.MAX_RATE
                            ),
                            'label-legend': this.label,
                        },
                        children: [
                            {
                                tagName: 'input',
                                attributes: {
                                    'min': 1,
                                    'max': 5,
                                    'step': 1,
                                    'id':
                                        this.getAttribute('id') ||
                                        Digest.randomId(),
                                    'value':
                                        this.ratingBar &&
                                        this.ratingBar.querySelector<UIRating>(
                                            'ui-rating'
                                        )?.value
                                            ? this.ratingBar.querySelector<UIRating>(
                                                  'ui-rating'
                                              )!.value
                                            : null,
                                    'data-event': 'ui-feedback-rating',
                                },
                            },
                        ],
                    },
                    {
                        tagName: 'ui-hint',
                        attributes: {
                            type: UIFeedback.Result.Error,
                            class: 'ui-feedback__rating-error-hint -hidden',
                        },
                        children: [
                            this.defineLabel(
                                this.labelMissingRating,
                                UIFeedback.labels.MISSING_RATING_ERROR
                            ),
                        ],
                    },
                ],
            },
        ];

        if (this.ratingActivated || this.autoExtended) {
            return configs.concat(this.buildExtension());
        }

        return configs;
    }

    /**
     * Build configs for 'simple' feedback layout
     * @returns {Array<ElementConfig>}
     */
    buildSimpleFeedbackElements(): Array<ElementConfig> {
        /**
         * @type {Array<ElementConfig>}
         */
        const configs: Array<ElementConfig> = [
            {
                tagName: 'h2',
                attributes: {
                    class: 'ui-feedback__heading',
                    tabindex: TabIndex.Inactive,
                },
                children: [
                    !this.ratingActivated
                        ? (this.label ?? null)
                        : this.defineLabel(
                              this.labelTextarea,
                              UIFeedback.labels.TEXTAREA_LABEL
                          ),
                ],
            },
        ];

        return configs.concat(
            !this.ratingActivated
                ? {
                      element: new UIButtonbar(),
                      attributes: {
                          alignment: 'center',
                      },
                      children: [
                          {
                              tagName: 'button',
                              children: [
                                  this.defineLabel(
                                      this.labelPositive,
                                      UIFeedback.labels.POSITIVE
                                  ),
                              ],
                              attributes: {
                                  'type': 'button',
                                  'name': 'ui-feedback-positive',
                                  'class': 'button -guiding',
                                  'data-event': 'ui-feedback-action',
                              },
                          },
                          {
                              tagName: 'button',
                              children: [
                                  this.defineLabel(
                                      this.labelNegative,
                                      UIFeedback.labels.NEGATIVE
                                  ),
                              ],
                              attributes: {
                                  'type': 'button',
                                  'name': 'ui-feedback-negative',
                                  'class': 'button -guiding',
                                  'data-event': 'ui-feedback-action',
                              },
                          },
                      ],
                  }
                : this.buildExtension()
        );
    }

    /**
     * Build config for layout with feedback result
     * @returns {Array<ElementConfig>}
     */
    buildResultElements(): Array<ElementConfig> {
        const configs: Array<ElementConfig> = [];

        if (this.result === UIFeedback.Result.Success) {
            configs.push({
                tagName: 'ui-empty-state',
                attributes: {
                    'illustration-type': 'bubble',
                    'severity': UIFeedback.Result.Success,
                    'heading': this.defineLabel(
                        this.labelSuccess,
                        UIFeedback.labels.SUCCESS_MESSAGE
                    ),
                },
                classList: {
                    'ui-feedback__success-image': true,
                },
                children: this.labelResult
                    ? [
                          {
                              tagName: 'p',
                              children: [
                                  document.createTextNode(this.labelResult),
                              ],
                          },
                      ]
                    : [],
            });
        }

        if (this.result === UIFeedback.Result.Error) {
            configs.push({
                tagName: 'ui-empty-state',
                attributes: {
                    'illustration-type': 'bubble',
                    'severity': UIFeedback.Result.Error,
                    'heading': this.defineLabel(
                        this.labelError,
                        UIFeedback.labels.ERROR_MESSAGE
                    ),
                },
                classList: {
                    'ui-feedback__error-image': true,
                },
                children: this.labelResult
                    ? [
                          {
                              tagName: 'p',
                              children: [
                                  document.createTextNode(this.labelResult),
                              ],
                          },
                      ]
                    : [],
            });
        }

        return configs;
    }

    /**
     * Reset feedback to build the first view with empty fields
     */
    reset(): void {
        this.ratingActivated = false;
        if (this.layout === UIFeedback.layouts.SIMPLE) {
            this._simpleRating = null;
        }

        if (this.result) {
            this.result = null;
            return;
        }

        if (this.ratingBar) {
            this.ratingBar.querySelector<UIRating>('ui-rating')!.value = null;
            // feedback labels are set in va-chatbox element, handle reset label text there
            this.dispatchCustomEvent('reset');
        }
        if (this.extension) {
            setData(this.extension, { 'ui-feedback-textarea': null });
            // dispatch change event to recheck field validity
            this.extTextarea?.dispatchEvent(new Event('input'));
        }

        this.rebuildChildren(this.buildFeedback());
    }

    updateTextCounter(): void {
        if (this.extTextarea) {
            const counter = this.querySelector<UITextCounter>('ui-textcounter');
            if (counter) {
                counter.updateCounter();
            }
        }
    }

    ensureSubmitValidity(): void {
        if (this.extTextarea) {
            const isValid = this.extTextarea.validity.valid;

            this.submitBtn.disabled = !isValid;
        }
    }

    /**
     * Shifts focus on the title after feedback is submitted
     * @private
     */
    private focusTitle(): void {
        if (this.result === UIFeedback.Result.Success) {
            const successHeading = this.querySelector<HTMLElement>(
                'ui-empty-state.ui-feedback__success-image .ui-empty-state__heading'
            );
            if (successHeading) {
                successHeading.focus();
            }
        }
    }

    /**
     * Handle click on positive/negative button inside simple layout
     * @private
     * @param  {DeclarativeEvent} e
     */
    handleFeedbackAction(e: CustomEvent) {
        switch (e.detail.relatedTarget.name) {
            case 'ui-feedback-positive':
                this.simpleRating = 'POSITIVE';
                break;
            case 'ui-feedback-negative':
                this.simpleRating = 'NEGATIVE';
                break;
            default:
                break;
        }
    }

    /**
     * Handle confirm feedback form
     * @private
     */
    private handleFeedbackConfirm(): void {
        if (!this.rating) {
            this.ratingErrorHint.show();
            return;
        }
        if (this.ratingErrorHint) {
            this.ratingErrorHint.hide();
        }
        this.dispatchCustomEvent('confirm', {
            rating: this.rating,
            text: this.extTextarea ? this.extTextarea.value : '',
            id: this.getAttribute('id'),
        });
    }

    /**
     * Handle cancel feedback form
     * @private
     */
    private handleFeedbackCancel(): void {
        this.dispatchCustomEvent('cancel', {
            rating: this.rating,
            text: this.extTextarea ? this.extTextarea.value : '',
            id: this.getAttribute('id'),
        });
    }

    /**
     * Handle rating bar change
     * @private
     */
    private handleRatingBarActivated(): void {
        if (this.ratingActivated) {
            return;
        }
        this.ratingActivated = true;
        this.rebuildChildren(this.buildFeedbackElements());
        if (this.ratingErrorHint) {
            this.ratingErrorHint.hide();
        }
        if (this.extTextarea) {
            this.extTextarea.focus();
        }
    }

    /**
     * Handle textarea input
     * @private
     */
    private handleTextareaInput(): void {
        this.ensureSubmitValidity();
    }

    /**
     * Build config for the feedback
     * @returns {Array<ElementConfig>}
     */
    buildFeedback(): Array<ElementConfig> {
        if (this.result) {
            return this.buildResultElements();
        }

        switch (this.layout) {
            case UIFeedback.layouts.SIMPLE:
                return this.buildSimpleFeedbackElements();
            default:
                return this.buildFeedbackElements();
        }
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: AttributeValue,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        /* istanbul ignore if */
        if (!this.hydrated) {
            return;
        }
        switch (name) {
            case 'label':
            case 'label-minrating':
            case 'label-maxrating':
            case 'label-textarea':
            case 'label-hint':
            case 'label-confirm':
            case 'result':
            case 'label-success':
            case 'label-error':
            case 'label-result':
            case 'label-positive':
            case 'label-negative':
            case 'label-message-placeholder':
            case 'label-limit-validation':
            case 'label-missing-rating':
            case 'extended':
            case 'auto-extended':
            case 'extension-layout':
            case 'overflow-maxlength':
                this.rebuildChildren(this.buildFeedback());
                this.focusTitle();
                this.updateTextCounter();
                this.ensureSubmitValidity();
                break;
            case 'text-maxlength':
            case 'layout':
                this.reset();
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /**
     * @private
     */
    private subscribeEvents(): void {
        this.clickObserver.observe(this);
        this.changeObserver.observe(this);
        this.inputObserver.observe(this);
        this.addEventListener('ui-feedback-action', this.feedbackActionHandler);
        this.addEventListener(
            'ui-feedback-confirm',
            this.feedbackConfirmHandler
        );
        this.addEventListener('ui-feedback-cancel', this.feedbackCancelHandler);
        this.addEventListener(
            'ui-feedback-rating',
            this.ratingBarActivatedHandler
        );
        this.addEventListener('ui-feedback-input', this.textareaInputHandler);
    }

    /**
     * @private
     */
    private unsubscribeEvents(): void {
        this.clickObserver.disconnect();
        this.changeObserver.disconnect();
        this.inputObserver.disconnect();
        this.removeEventListener(
            'ui-feedback-action',
            this.feedbackActionHandler
        );
        this.removeEventListener(
            'ui-feedback-confirm',
            this.feedbackConfirmHandler
        );
        this.removeEventListener(
            'ui-feedback-cancel',
            this.feedbackCancelHandler
        );
        this.removeEventListener(
            'ui-feedback-rating',
            this.ratingBarActivatedHandler
        );
        this.removeEventListener(
            'ui-feedback-input',
            this.textareaInputHandler
        );
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        this.ratingActivated = false;
        this.insertElements(this.buildFeedback());
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        this.subscribeEvents();
    }
}
